import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, Camera, Video, Plus, Loader2, X, ShieldAlert, Trash2, Upload, MapPin, Map as MapIcon } from "lucide-react";

const CATEGORIES = [
  { key: "violence",             label: "Violence",              icon: "⚡", color: "#FF453A" },
  { key: "ballot_snatching",     label: "Ballot snatching",      icon: "🗳",  color: "#FF453A" },
  { key: "over_voting",          label: "Over-voting",           icon: "📊", color: "#FF9F0A" },
  { key: "obstruction",          label: "Obstruction",           icon: "⛔", color: "#FF9F0A" },
  { key: "equipment_malfunction",label: "Equipment malfunction", icon: "⚙️", color: "#5AC8FA" },
  { key: "bribery",              label: "Vote-buying / bribery", icon: "💰", color: "#FF453A" },
  { key: "intimidation",         label: "Intimidation",          icon: "😰", color: "#FF9F0A" },
  { key: "misconduct",           label: "Officer misconduct",    icon: "🚨", color: "#FF9F0A" },
  { key: "media_denied",         label: "Media/observer denied", icon: "📸", color: "#FF9F0A" },
  { key: "underage_voting",      label: "Under-age voting",      icon: "🔞", color: "#FF9F0A" },
  { key: "impersonation",        label: "Voter impersonation",   icon: "🎭", color: "#FF453A" },
  { key: "other",                label: "Other",                 icon: "•",  color: "#71717A" },
];
const SEVERITY = {
  low:      { label: "Low",      color: "#5AC8FA" },
  medium:   { label: "Medium",   color: "#FF9F0A" },
  high:     { label: "High",     color: "#FF453A" },
  critical: { label: "Critical", color: "#AF52DE" },
};

export default function IncidentReports() {
  const { user } = useAuth();
  const canReview = user?.role === "admin" || user?.role === "observer";
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ status: "", category: "", severity: "" });
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);

  const [form, setForm] = useState({ category: "violence", severity: "medium", title: "", description: "", media_paths: [] });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [geo, setGeo] = useState({ lat: null, lng: null });
  const [activeElection, setActiveElection] = useState(null);
  const [dismissTarget, setDismissTarget] = useState(null);
  const [dismissNote, setDismissNote] = useState("");

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(filter).filter(([_, v]) => v));
    const { data } = await api.get("/incidents", { params });
    setItems(data);
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/elections").then(({ data }) => {
      const a = data.find(e => e.status === "active") || data[0];
      if (a) setActiveElection(a);
    }).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const i of items) g[i.category] = (g[i.category] || 0) + 1;
    return g;
  }, [items]);

  const captureGeo = () => {
    setLocating(true);
    if (!navigator.geolocation) { setLocating(false); toast.error("Location not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { setGeo({ lat: p.coords.latitude, lng: p.coords.longitude }); setLocating(false); toast.success("Location captured"); },
      (e) => { setLocating(false); toast.error("Location denied"); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const onMedia = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/upload?is_public=false", fd, { headers: { "Content-Type": "multipart/form-data" }});
        setForm(fm => ({ ...fm, media_paths: [...fm.media_paths, data.path] }));
      } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    }
    setUploading(false);
    toast.success(`${files.length} file${files.length>1?"s":""} attached`);
  };

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/incidents", { ...form, election_id: activeElection?.id || null, lat: geo.lat, lng: geo.lng });
      toast.success("Incident reported");
      setForm({ category: "violence", severity: "medium", title: "", description: "", media_paths: [] });
      setGeo({ lat: null, lng: null });
      setShowForm(false); load();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, status, note = "") => {
    try { await api.patch(`/incidents/${id}/status`, { status, note }); toast.success(`Marked ${status}`); load(); setSelected(null); }
    catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1600px]">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Reporting Engine</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2" data-testid="incidents-title">Incidents</h1>
          <p className="text-zinc-400 text-sm mt-2">Log violations, misconduct or malfunctions with photo, video and geolocation evidence.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/incidents/map" data-testid="open-incident-map"
            className="px-3 py-2.5 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10 flex items-center gap-2">
            <MapIcon size={14}/> Map view
          </Link>
          <button onClick={()=>setShowForm(true)} data-testid="report-incident-btn"
            className="px-4 py-2.5 bg-red-500 text-white text-xs uppercase tracking-[0.2em] font-semibold hover:bg-red-400 flex items-center gap-2">
            <ShieldAlert size={14}/> Report incident
          </button>
        </div>
      </div>

      {/* Summary grid */}
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {CATEGORIES.map(c => (
          <button key={c.key} onClick={()=>setFilter(f=>({...f, category: f.category===c.key ? "" : c.key}))}
            className={`border p-4 text-left transition-colors ${filter.category===c.key ? "border-white bg-white/5" : "border-white/10 hover:bg-white/[0.03]"}`}
            data-testid={`cat-${c.key}`}>
            <div className="text-lg">{c.icon}</div>
            <div className="text-xs uppercase tracking-wider text-zinc-500 mt-2">{c.label}</div>
            <div className={`font-display text-3xl tabular mt-1 ${!grouped[c.key] ? "opacity-30" : ""}`} style={{ color: c.color }} data-testid={`cat-count-${c.key}`}>{grouped[c.key] || 0}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-2 text-xs">
        <span className="uppercase tracking-[0.2em] text-zinc-500 mr-2">Filter</span>
        <select value={filter.status} onChange={e=>setFilter(f=>({...f, status:e.target.value}))} className="bg-transparent border border-white/20 px-3 py-1.5 outline-none" data-testid="filter-status">
          <option value="" className="bg-black">Any status</option>
          <option value="open" className="bg-black">Open</option>
          <option value="acknowledged" className="bg-black">Acknowledged</option>
          <option value="investigating" className="bg-black">Investigating</option>
          <option value="resolved" className="bg-black">Resolved</option>
          <option value="dismissed" className="bg-black">Dismissed</option>
        </select>
        <select value={filter.severity} onChange={e=>setFilter(f=>({...f, severity:e.target.value}))} className="bg-transparent border border-white/20 px-3 py-1.5 outline-none" data-testid="filter-severity">
          <option value="" className="bg-black">Any severity</option>
          {Object.entries(SEVERITY).map(([k,v])=><option key={k} value={k} className="bg-black">{v.label}</option>)}
        </select>
        {(filter.status||filter.category||filter.severity) && <button onClick={()=>setFilter({status:"",category:"",severity:""})} className="underline text-zinc-400">clear</button>}
      </div>

      {/* List */}
      <div className="mt-6 border border-white/10 data-table">
        <div className="grid grid-cols-12 px-4 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          <div className="col-span-1">Sev</div>
          <div className="col-span-3">Category</div>
          <div className="col-span-4">Title</div>
          <div className="col-span-2">Reporter</div>
          <div className="col-span-2 text-right">Status · Time</div>
        </div>
        <div className="divide-y divide-white/5">
          {items.map(i => {
            const c = CATEGORIES.find(x => x.key === i.category) || CATEGORIES[CATEGORIES.length-1];
            const sev = SEVERITY[i.severity] || { label: i.severity || "Unknown", color: "#71717A" };
            return (
              <button key={i.id} onClick={()=>setSelected(i)} data-testid={`incident-${i.id}`}
                className="w-full text-left grid grid-cols-12 px-4 py-3 items-center hover:bg-white/[0.03]">
                <div className="col-span-1"><span className="text-[10px] uppercase tracking-wider px-2 py-0.5" style={{ background: sev.color, color: "#000" }}>{sev.label}</span></div>
                <div className="col-span-3 flex items-center gap-2 text-sm"><span>{c.icon}</span>{c.label}</div>
                <div className="col-span-4 text-sm truncate">{i.title}
                  {i.media_paths?.length > 0 && <span className="ml-2 text-[10px] text-emerald-400">📎 {i.media_paths.length}</span>}
                </div>
                <div className="col-span-2 text-xs text-zinc-400 truncate">{(i.reporter_email||"").split("@")[0]}</div>
                <div className="col-span-2 text-right text-xs">
                  <div className={`inline-block px-2 py-0.5 uppercase tracking-wider text-[10px] ${i.status==="resolved"?"bg-emerald-500/20 text-emerald-300":i.status==="dismissed"?"bg-zinc-500/20 text-zinc-400":"bg-red-500/20 text-red-300"}`}>{i.status}</div>
                  <div className="text-zinc-500 mt-0.5">{(i.created_at||"").replace("T"," ").slice(0,16)}</div>
                </div>
              </button>
            );
          })}
          {!items.length && <div className="p-8 text-zinc-500 text-sm text-center">No incidents match. Everything's calm so far.</div>}
        </div>
      </div>

      {/* Report form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={()=>setShowForm(false)} data-testid="report-modal">
          <form onSubmit={submit} onClick={e=>e.stopPropagation()} className="bg-[#0F0F12] border border-red-500/40 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-red-400"><ShieldAlert size={14}/> New incident</div>
            <h2 className="font-display text-3xl mt-2 mb-4">Report an incident</h2>

            <label className="block text-xs uppercase tracking-[0.2em] text-zinc-500 mt-3 mb-2">Category</label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {CATEGORIES.map(c => (
                <button type="button" key={c.key} onClick={()=>setForm(f=>({...f, category:c.key}))}
                  data-testid={`form-cat-${c.key}`}
                  className={`px-3 py-2 border text-xs text-left ${form.category===c.key ? "border-red-400 bg-red-500/10 text-red-200" : "border-white/15 hover:bg-white/5"}`}>
                  <div>{c.icon}</div>
                  <div className="mt-1">{c.label}</div>
                </button>
              ))}
            </div>

            <label className="block text-xs uppercase tracking-[0.2em] text-zinc-500 mt-4 mb-2">Severity</label>
            <div className="flex gap-2">
              {Object.entries(SEVERITY).map(([k,v]) => (
                <button type="button" key={k} onClick={()=>setForm(f=>({...f, severity:k}))} data-testid={`form-sev-${k}`}
                  className={`flex-1 px-3 py-2 text-xs uppercase tracking-[0.15em] border ${form.severity===k ? "text-black font-semibold" : "border-white/15 hover:bg-white/5"}`}
                  style={form.severity===k ? { background: v.color, borderColor: v.color } : {}}>{v.label}</button>
              ))}
            </div>

            <input required value={form.title} onChange={e=>setForm(f=>({...f, title:e.target.value}))}
              placeholder="Short title (e.g., 'Party agent removed from PU')" data-testid="form-title"
              className="w-full mt-4 bg-transparent border-b border-white/20 py-2 outline-none focus:border-white text-lg"/>
            <textarea value={form.description} onChange={e=>setForm(f=>({...f, description:e.target.value}))} rows={4}
              placeholder="Describe what happened, who was involved, and when — as objectively as possible." data-testid="form-description"
              className="w-full mt-3 bg-transparent border border-white/15 p-3 outline-none focus:border-white text-sm resize-none"/>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <label className="cursor-pointer border border-white/20 hover:bg-white/10 px-3 py-2 text-xs uppercase tracking-[0.2em] flex items-center gap-2" data-testid="media-upload-label">
                <input type="file" multiple accept="image/*,video/*" className="hidden" onChange={onMedia} data-testid="media-input"/>
                {uploading ? <Loader2 size={13} className="animate-spin"/> : <Upload size={13}/>}
                {uploading ? "Uploading..." : "Add photo/video"}
              </label>
              <button type="button" onClick={captureGeo} data-testid="capture-geo-btn"
                className="border border-white/20 hover:bg-white/10 px-3 py-2 text-xs uppercase tracking-[0.2em] flex items-center gap-2">
                {locating ? <Loader2 size={13} className="animate-spin"/> : <MapPin size={13}/>}
                {geo.lat ? `${geo.lat.toFixed(4)}, ${geo.lng.toFixed(4)}` : "Capture location"}
              </button>
              {form.media_paths.length > 0 && <div className="text-xs text-emerald-400">📎 {form.media_paths.length} attached</div>}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={()=>setShowForm(false)} className="px-4 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10">Cancel</button>
              <button type="submit" disabled={saving || !form.title} data-testid="submit-incident-btn"
                className="px-4 py-2 bg-red-500 text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-red-400 disabled:opacity-40 flex items-center gap-2">
                {saving ? <Loader2 size={13} className="animate-spin"/> : <ShieldAlert size={13}/>}
                {saving ? "Filing" : "File incident"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Dismiss reason modal */}
      {dismissTarget && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={()=>setDismissTarget(null)} data-testid="dismiss-modal">
          <div onClick={e=>e.stopPropagation()} className="bg-[#0F0F12] border border-white/20 max-w-md w-full p-6">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Dismiss incident</div>
            <h3 className="font-display text-2xl mt-2">Why are you dismissing?</h3>
            <textarea autoFocus value={dismissNote} onChange={e=>setDismissNote(e.target.value)} rows={3}
              placeholder="Brief reason (e.g., duplicate report, unverified claim)…" data-testid="dismiss-note"
              className="w-full mt-4 bg-transparent border border-white/15 p-3 outline-none focus:border-white text-sm resize-none"/>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={()=>setDismissTarget(null)} className="px-4 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10">Cancel</button>
              <button onClick={()=>{ const id = dismissTarget; setDismissTarget(null); updateStatus(id, "dismissed", dismissNote); }} data-testid="confirm-dismiss-btn"
                className="px-4 py-2 bg-red-500 text-white text-xs uppercase tracking-[0.2em] font-semibold hover:bg-red-400">Confirm dismiss</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={()=>setSelected(null)} data-testid="detail-modal">
          <div onClick={e=>e.stopPropagation()} className="relative bg-[#0F0F12] border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
            {(() => {
              const c = CATEGORIES.find(x => x.key === selected.category) || CATEGORIES[CATEGORIES.length-1];
              const sev = SEVERITY[selected.severity] || { label: selected.severity || "Unknown", color: "#71717A" };
              return <>
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em]">
                  <span className="px-2 py-0.5 text-[10px] font-semibold text-black" style={{ background: sev.color }}>{sev.label}</span>
                  <span className="text-zinc-500">{c.icon} {c.label}</span>
                </div>
                <h2 className="font-display text-3xl mt-2">{selected.title}</h2>
                <div className="text-xs text-zinc-500 mt-1">{selected.reporter_email} · {(selected.created_at||"").replace("T"," ").slice(0,19)}</div>
                <p className="text-sm text-zinc-300 mt-4 whitespace-pre-wrap leading-relaxed">{selected.description || "(no description)"}</p>
                {selected.lat && <div className="mt-3 text-xs text-zinc-400">📍 <a href={`https://www.google.com/maps?q=${selected.lat},${selected.lng}`} target="_blank" rel="noreferrer" className="underline">{selected.lat.toFixed(5)}, {selected.lng.toFixed(5)}</a></div>}
                {selected.media_paths?.length > 0 && (
                  <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {selected.media_paths.map(p => {
                      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}?auth=${localStorage.getItem("ec_token")}`;
                      const isVideo = /\.(mp4|mov|webm)$/i.test(p);
                      return isVideo
                        ? <video key={p} src={url} controls className="w-full aspect-square object-cover bg-black"/>
                        : <a key={p} href={url} target="_blank" rel="noreferrer"><img src={url} alt="" className="w-full aspect-square object-cover"/></a>;
                    })}
                  </div>
                )}
                {selected.history?.length > 0 && (
                  <div className="mt-5 border-t border-white/10 pt-3">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">History</div>
                    {selected.history.map((h,i) => (
                      <div key={`${h.status}-${h.at}-${i}`} className="text-xs text-zinc-400 mb-1"><span className="uppercase tracking-wider text-white">{h.status}</span> — {h.by_email} · {(h.at||"").replace("T"," ").slice(0,16)} {h.note && `— ${h.note}`}</div>
                    ))}
                  </div>
                )}
                {canReview && selected.status !== "resolved" && selected.status !== "dismissed" && (
                  <div className="mt-6 flex flex-wrap gap-2 border-t border-white/10 pt-4">
                    <button onClick={()=>updateStatus(selected.id, "acknowledged")} data-testid="ack-btn" className="px-3 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10">Acknowledge</button>
                    <button onClick={()=>updateStatus(selected.id, "investigating")} data-testid="invest-btn" className="px-3 py-2 border border-amber-400/40 text-amber-300 text-xs uppercase tracking-[0.2em] hover:bg-amber-400/10">Investigating</button>
                    <button onClick={()=>updateStatus(selected.id, "resolved")} data-testid="resolve-btn" className="px-3 py-2 bg-emerald-500 text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-emerald-400">Resolve</button>
                    <button onClick={()=>{ setDismissTarget(selected.id); setDismissNote(""); }} data-testid="dismiss-btn" className="px-3 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10 ml-auto">Dismiss</button>
                  </div>
                )}
                <button onClick={()=>setSelected(null)} className="absolute top-4 right-4 p-2 text-zinc-500 hover:text-white" data-testid="close-detail"><X size={16}/></button>
              </>;
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
